import { CodeInvoiceStatus, EmbeddedCode } from '../shared/code';

/**
 * Finance marks on a Job or a Stage. Absent means not marked, so there is nothing to migrate.
 *
 * A mark set on the job is copied onto every stage under it, which is why the item line's own stage
 * is enough to answer whether the line is locked.
 */
export interface FinanceMarks {
    nonBillable?: boolean;
    nonPayable?: boolean;
}

/** Who forced a line's answer. `'free'` means nobody did and the line owns its own answer. */
export type FinanceLockSource = 'free' | 'job' | 'stage';

/** Which answer a mark command acts on. The two sides are independent and never write each other's fields. */
export type FinanceMarkSide = 'billable' | 'payable';

/**
 * What a mark or unmark did, shown in the confirmation dialog before the write and recorded on the
 * activity after it.
 */
export interface FinanceMarkCounts {
    itemsUpdated: number;
    /**
     * Lines left alone because their invoice, bill or purchase order is already Approved or Error.
     * The count is the only way a user learns those still need handling by hand — cancelled lines
     * are excluded, or it would send them hunting for documents that do not exist.
     */
    itemsSkipped: number;
    /** Only set by the job-level command, which marks every stage under it. */
    stagesMarked?: number;
    /** Draft invoices or bills the cascade emptied and therefore deleted. */
    draftDocumentsDeleted?: number;
    /** Lines the bulk write refused (a readonly item status, say) and so still need attention. */
    itemsFailed?: number;
}

/**
 * The lock resolved per side and carried on the item DTO, so the update guard and the editor's
 * readonly policy gate on one value instead of each deriving its own.
 */
export interface ItemFinanceLock {
    billable: FinanceLockSource;
    payable: FinanceLockSource;
}

/** The item fields a mark forces — picked off the model so a rename there fails the compile here. */
type FinanceMarkableItem = Pick<EmbeddedCode, 'codeInvoicingStatus' | 'sellPrice' | 'customSellPrice' | 'payable' | 'buyPrice' | 'customBuyPrice'>;

export type MarkedScope = { finance?: FinanceMarks } | null | undefined;

/**
 * Whether either side is actually marked.
 *
 * Guards the sparseness rule the schema depends on: an empty or all-false `finance` must never be
 * stored, or an unmarked job starts materialising the key and "absent = unmarked" stops holding.
 */
export function hasFinanceMark(marks: FinanceMarks | null | undefined): boolean {

    return marks?.nonBillable === true || marks?.nonPayable === true;
}

/**
 * Job first for attribution, not for the answer: the job's mark is copied onto its stages, so either
 * order returns a lock. Only the hint telling a user to unmark it on the job depends on the name.
 */
function resolveLockSource(jobMark?: boolean, stageMark?: boolean): FinanceLockSource {

    return jobMark ? 'job' : stageMark ? 'stage' : 'free';
}

/**
 * The marks in force on a line under this job and stage — the union of the two, since a job's mark
 * applies to every stage beneath it.
 *
 * Null when neither side is marked, so a create path can skip the forcing entirely instead of
 * passing an all-false mark that forces nothing.
 */
export function effectiveFinanceMarks(job: MarkedScope, stage: MarkedScope): FinanceMarks | null {

    const marks: FinanceMarks = {
        ...(job?.finance?.nonBillable || stage?.finance?.nonBillable ? { nonBillable: true } : {}),
        ...(job?.finance?.nonPayable || stage?.finance?.nonPayable ? { nonPayable: true } : {}),
    };

    return hasFinanceMark(marks) ? marks : null;
}

/** Answers "is this line's answer forced, and by whom" for one job/stage pair. */
export function resolveItemFinanceLock(job: MarkedScope, stage: MarkedScope): ItemFinanceLock {

    return {
        billable: resolveLockSource(job?.finance?.nonBillable, stage?.finance?.nonBillable),
        payable: resolveLockSource(job?.finance?.nonPayable, stage?.finance?.nonPayable),
    };
}

/**
 * What a mark does to a line. The zero, not the flag, is what keeps the line out of the invoice, the
 * bill and job cost — the totals have no flag to consult. The custom flag is cleared so the unmark
 * re-price is free to price from the pricebook again.
 *
 * A scope mark, not a line's own answer: `applyFinanceAnswersToItem` holds the same sell/buy
 * invariant for a caller's own patch, and the shared stage item write applies it.
 */
export function applyFinanceMarksToItem<T extends FinanceMarkableItem>(item: T, marks: FinanceMarks | null | undefined): T {

    return {
        ...item,
        ...(marks?.nonBillable ? { codeInvoicingStatus: CodeInvoiceStatus.NotBillable, sellPrice: 0, customSellPrice: false } : {}),
        ...(marks?.nonPayable ? { payable: false, buyPrice: 0, customBuyPrice: false } : {}),
    };
}

/**
 * What a line's own answers do to its prices: Not Billable zeroes sell, Not Payable zeroes buy, and
 * the custom flag goes with the price so a later re-price is free to read the pricebook again. No
 * Charge is kept on the sell side — the option is retired from the editors, but a line still holding
 * it must keep pricing to zero.
 *
 * Owned here, and applied by the shared stage item write, because the legacy Items route reaches that
 * write without passing a CQRS handler: a Not Payable answer saved that way used to leave a live buy
 * price loading job cost. Only an explicit `false` counts — absent `payable` reads as payable.
 */
export function applyFinanceAnswersToItem<T extends FinanceMarkableItem>(item: T): T {

    const notBillable = item.codeInvoicingStatus === CodeInvoiceStatus.NoCharge || item.codeInvoicingStatus === CodeInvoiceStatus.NotBillable;

    return {
        ...item,
        ...(notBillable ? { sellPrice: 0, customSellPrice: false } : {}),
        ...(item.payable === false ? { buyPrice: 0, customBuyPrice: false } : {}),
    };
}
